import React, { createContext, useState, useContext, ReactNode } from 'react';
import ScorecardApiService from '../apiService/scorecardApiService';
import { RevenueOperationsScorecard } from '../types';

// ----------- Type Definitions -----------
interface ScorecardContextType {
  loading: boolean;
  error: string;
  scorecardData: RevenueOperationsScorecard | null;
  getRevenueOperationsScorecard: (dealerId: string) => Promise<void>;
}

// ----------- Context Create -----------
export const ScorecardContext = createContext<ScorecardContextType | undefined>(undefined);

// ----------- Provider Component -----------
export const ScorecardProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [scorecardData, setScorecardData] = useState<RevenueOperationsScorecard | null>(null);

  // ----------- API Calls Using Scorecard Service -----------
  const getRevenueOperationsScorecard = async (dealerId: string) => {
    setLoading(true);
    setError('');
    try {
      const response = await ScorecardApiService.getRevenueOperationsScorecard(dealerId);
      if (response.body) {
        setScorecardData(response.body);
      } else {
        throw new Error('No data received from API');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch scorecard data';
      setError(errorMessage);
      console.error('Error fetching scorecard:', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScorecardContext.Provider
      value={{
        loading,
        error,
        scorecardData,
        getRevenueOperationsScorecard,
      }}
    >
      {children}
    </ScorecardContext.Provider>
  );
};

// ----------- Custom Hook -----------
export const useScorecard = (): ScorecardContextType => {
  const context = useContext(ScorecardContext);
  if (context === undefined) {
    throw new Error('useScorecard must be used within a ScorecardProvider');
  }
  return context;
};

